import { Canvas, type CanvasProps, useFrame } from '@react-three/fiber';
import { type CSSProperties, type ReactNode, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { PCFSoftShadowMap } from 'three';

import Text from '@cartwhl/ui/base/Text';

import { PlayerFramingMetricsProvider } from '../hooks/playerFramingMetricsContext';
import { usePlayerSettingsContext } from '../hooks/playerSettingsContext';
import { usePlayerStoreApi } from '../store/playerStore';
import PlayerDebugStats from './PlayerDebugStats';
import ReferenceNameTags from './ReferenceNameTags';

const PLAYER_CANVAS_RESIZE_DEBOUNCE_MS = 50;

export interface PlayerCanvasProps {
  children: ReactNode;
  className?: string;
  onActorNameTagClick?: (actorId: string) => void;
  style?: CSSProperties;
  onPointerMissed?: CanvasProps['onPointerMissed'];
}

function CanvasError() {
  return (
    <Text variant="studio/body/sm" className="text-secondary">
      Unable to render 3D canvas.
    </Text>
  );
}

function PlayerPlaybackTicker() {
  const store = usePlayerStoreApi();
  const {
    settings: { playback },
  } = usePlayerSettingsContext();

  useFrame((_, delta) => {
    store.updatePlayback(delta, playback);
  }, -1);

  return null;
}

export default function PlayerCanvas({
  children,
  className,
  onActorNameTagClick,
  style,
  onPointerMissed,
}: PlayerCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={containerRef}
      className={`bg-gray-30 dark:bg-gray-75 relative flex h-full w-full flex-none items-center justify-center overflow-hidden rounded-3xl ${className ?? ''}`}
      style={style}
    >
      <ErrorBoundary fallback={<CanvasError />}>
        <Canvas
          id="cartwhl-player-canvas"
          gl={{ preserveDrawingBuffer: false }}
          resize={{
            // Avoid reallocating large WebGL buffers for every ResizeObserver tick during window drags.
            debounce: { resize: PLAYER_CANVAS_RESIZE_DEBOUNCE_MS, scroll: 0 },
          }}
          style={{ borderRadius: 'inherit' }}
          onPointerMissed={onPointerMissed}
          shadows={{
            enabled: true,
            autoUpdate: true,
            type: PCFSoftShadowMap,
          }}
        >
          <PlayerFramingMetricsProvider>
            <PlayerPlaybackTicker />
            {children}
            <ReferenceNameTags onActorClick={onActorNameTagClick} />
            <PlayerDebugStats parent={containerRef} />
          </PlayerFramingMetricsProvider>
        </Canvas>
      </ErrorBoundary>
    </div>
  );
}
